import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Crown, PlayCircle, Check, Users, PlayCircle as Play, Clock, Layers } from 'lucide-react';
import { useLang, tx } from '../../i18n/LanguageContext';
import api from '../../lib/api';

const IMG = 'https://static.prod-images.emergentagent.com/jobs/2f8d28b7-4ebe-4317-8ba5-2c9c19007ec7/images/';
export const HERO_SIM_IMG = `${IMG}ae99617e9223c29f794da9ac5420a5491e7c49c6aa88e3fc292e9ed9c96dfb8e.jpeg`;

export const useFirstFreeSim = () => {
  const [to, setTo] = useState('/simulations');
  useEffect(() => {
    api.get('/simulations').then(({ data }) => {
      const s = (data || []).find(x => x.is_free && x.has_html);
      if (s) setTo(`/simulations/${s.id}`);
    }).catch(() => {});
  }, []);
  return to;
};

const HeroStats = () => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const stats = [
    { Icon: Users, value: L('+15K', '+15K'), label: L('طالب ومشترك', 'Students & subscribers'), key: 'students' },
    { Icon: Play, value: L('+70', '+70'), label: L('كورس احترافي', 'Professional courses'), key: 'courses' },
    { Icon: Clock, value: L('+120', '+120'), label: L('ساعة من المحتوى', 'Hours of content'), key: 'hours' },
    { Icon: Layers, value: L('+500', '+500'), label: L('مشروع عملي', 'Practical projects'), key: 'projects' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-5 mt-10 sm:mt-12">
      {stats.map((s, i) => (
        <div key={s.key} data-testid={`hero-stat-${s.key}`} className="fade-up flex items-center justify-between gap-3 bg-white rounded-2xl border border-slate-200/80 p-4 sm:p-5 shadow-sm hover:shadow-md hover:-translate-y-1 transition-[transform,box-shadow] duration-300" style={{ animationDelay: `${i * 80}ms` }}>
          <div>
            <div className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight" dir="ltr">{s.value}</div>
            <div className="text-xs sm:text-sm font-semibold text-slate-500 mt-1">{s.label}</div>
          </div>
          <s.Icon className="w-6 h-6 sm:w-7 sm:h-7 text-blue-600 shrink-0" />
        </div>
      ))}
    </div>
  );
};

const HomeHero = () => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const simTo = useFirstFreeSim();
  const perks = [L('محاكاة تفاعلية', 'Interactive simulations'), L('محتوى عملي', 'Practical content'), L('تعلّم موجّه', 'Guided learning')];
  return (
    <section data-testid="home-hero" className="relative pt-[5.5rem] sm:pt-24 pb-8 sm:pb-10 overflow-hidden bg-gradient-to-b from-blue-50/80 via-slate-50 to-slate-50">
      <div className="absolute inset-0 hero-grid pointer-events-none" />
      <div className="site-container relative">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="fade-up">
            <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] font-black text-slate-900 leading-[1.25] tracking-tight mb-5">
              {L('تعلّم الأتمتة الصناعية', 'Learn industrial automation')}<br />
              <span className="text-blue-600">{L('بطريقة تفاعلية', 'the interactive way')}</span>
            </h1>
            <p data-testid="hero-tagline" className="inline-block px-4 py-2 rounded-xl bg-amber-100 text-slate-900 font-black text-base sm:text-xl mb-5">
              {L('لا تكتفِ بالمشاهدة... جرّب المحاكاة بنفسك', "Don't just watch... try the simulation yourself")}
            </p>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl mb-7">
              {L('تعلّم PLC و Factory I/O و SCADA والأتمتة الصناعية من خلال محاكاة تفاعلية واقعية وتطبيق عملي خطوة بخطوة.', 'Learn PLC, Factory I/O, SCADA and industrial automation through realistic interactive simulations and step-by-step practice.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Link to="/subscription" data-testid="hero-subscribe-cta" className="btn-subscribe px-8 h-12"><Crown className="w-5 h-5" />{L('اشترك الآن', 'Subscribe now')}</Link>
              <Link to={simTo} data-testid="hero-try-sim-cta" className="inline-flex items-center justify-center gap-2 bg-white hover:bg-blue-50 text-blue-700 font-bold px-7 h-12 rounded-xl border-2 border-blue-200 transition-colors">
                <PlayCircle className="w-5 h-5" />{L('جرّب المحاكاة', 'Try a simulation')}
              </Link>
            </div>
            <ul className="flex flex-wrap gap-x-6 gap-y-2">
              {perks.map(p => <li key={p} className="flex items-center gap-1.5 text-sm font-semibold text-slate-600"><Check className="w-4 h-4 text-blue-600" />{p}</li>)}
            </ul>
          </div>
          <div className="relative fade-up" style={{ animationDelay: '150ms' }}>
            <span className="absolute -top-3 start-6 z-10 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-400 text-slate-900 text-sm font-black shadow-lg shadow-amber-400/30">
              <Crown className="w-4 h-4" />{L('ميزة حصرية', 'Exclusive feature')}
            </span>
            <div className="rounded-3xl bg-white p-2.5 border border-slate-200 shadow-2xl shadow-slate-300/50">
              <img src={HERO_SIM_IMG} alt={L('محاكاة تفاعلية لخط إنتاج', 'Interactive production line simulation')} className="w-full aspect-[3/2] object-cover rounded-2xl" />
            </div>
            <Link to={simTo} data-testid="hero-watch-sim" className="absolute -bottom-4 end-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-slate-200 shadow-xl text-sm font-bold text-blue-700 float-slow">
              <span className="w-7 h-7 rounded-full bg-blue-600 text-white flex items-center justify-center"><PlayCircle className="w-4 h-4" /></span>
              {L('شاهد المحاكاة في العمل', 'See it in action')}
            </Link>
          </div>
        </div>
        <HeroStats />
      </div>
    </section>
  );
};

export default HomeHero;
